import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
const [recaptchaToken, setRecaptchaToken] = useState(null);

useEffect(() => {
  window.onContactRecaptchaChange = (token) => setRecaptchaToken(token);
  window.onContactRecaptchaExpired = () => setRecaptchaToken(null);
  const SRC = 'https://www.google.com/recaptcha/api.js';
  if (!document.querySelector(`script[src="${SRC}"]`)) {
    const el = document.createElement('script');
    el.src = SRC; el.async = true; el.defer = true;
    document.body.appendChild(el);
  } else if (window.grecaptcha && window.grecaptcha.render) {
    const box = document.querySelector('.g-recaptcha');
    if (box && !box.hasChildNodes()) {
      try { window.grecaptcha.render(box); } catch (err) {}
    }
  }
  return () => {
    delete window.onContactRecaptchaChange;
    delete window.onContactRecaptchaExpired;
  };
}, []);
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
  alert('Please complete the reCAPTCHA verification.');
  return;
}
    setSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken })
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        alert('There was an error sending your message. Please try emailing us directly.');
      }
    } catch (err) {
      alert('There was an error sending your message. Please try emailing us directly at info@serpfinity.ai');
    }
    if (window.grecaptcha && window.grecaptcha.reset) {
  try { window.grecaptcha.reset(); } catch (err) {}
}
setRecaptchaToken(null);
setSending(false);
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact SERPfinity",
    "description": "Contact the SERPfinity team for support with AI SEO tools, account questions, or partnership inquiries.",
    "url": "https://app.serpfinity.ai/contact",
    "mainEntity": {
        "@type": "Organization",
        "name": "SERPfinity",
        "url": "https://app.serpfinity.ai",
        "logo": "https://app.serpfinity.ai/logo192.png",
        "email": "info@serpfinity.ai",
        "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "info@serpfinity.ai",
            "availableLanguage": [
                "English"
            ],
            "areaServed": "Worldwide"
        }
    }
};
  // Schema injection via useEffect
  useEffect(() => {
    const schemas = [contactPageSchema];
    const scriptElements = [];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });
    return () => {
      scriptElements.forEach(el => el.remove());
    };
  }, []);


  return (
    <>
      <Helmet>
        <title>Contact SERPfinity | Get Help with AI SEO Tools</title>
        <meta name="description" content="Contact the SERPfinity team for support with AI SEO tools, account questions, or partnership inquiries. We typically respond within 24-48 business hours." />
            <meta property="og:title" content="Contact SERPfinity | Get Help with AI SEO Tools" />
            <meta property="og:description" content="Contact the SERPfinity team for support with AI SEO tools, account questions, or partnership inquiries. We typically respond within 24-48 business hours." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai/contact" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta property="og:site_name" content="SERPfinity" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Contact SERPfinity | Get Help with AI SEO Tools" />
            <meta name="twitter:description" content="Contact the SERPfinity team for support with AI SEO tools, account questions, or partnership inquiries." />
            <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />



      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-lg text-blue-200">
            Have a question or need help? We'd love to hear from you.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12">

          {/* Contact Info Cards */}
          <div className="space-y-8">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Email Us</h3>
              <p className="text-slate-600 text-sm mb-2">For general inquiries and support</p>
              <a href="mailto:info@serpfinity.ai" className="text-blue-600 hover:text-blue-800 font-medium">info@serpfinity.ai</a>
            </div>

            <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Response Time</h3>
              <p className="text-slate-600 text-sm">We typically respond within 24-48 hours during business days.</p>
            </div>

            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Help Center</h3>
              <p className="text-slate-600 text-sm mb-2">Browse our guides and FAQs for quick answers.</p>
              <Link to="/help" className="text-green-600 hover:text-green-800 font-medium">Visit Help Center </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            {submitted ? (
              <div className="p-8 bg-green-50 border border-green-200 rounded-xl text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">Message Sent!</h3>
                <p className="text-green-700">Thank you for reaching out. We'll get back to you within 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                      placeholder="How can we help?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing & Subscriptions</option>
                      <option value="feature">Feature Request</option>
                      <option value="bug">Bug Report</option>
                      <option value="partnership">Partnership / Business</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                    placeholder="Tell us more about how we can help you..."
                  />
                </div>

                <div className="g-recaptcha" style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }} data-sitekey="6LeVT4wsAAAAAIS-5vjTS593TPXr0PurHSaM7pB-" data-callback="onContactRecaptchaChange" data-expired-callback="onContactRecaptchaExpired"></div>
                <button
                  type="submit"
                  disabled={sending || !recaptchaToken}
                  className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
      </>
  );
};

export default ContactUs;
